// City / unit / marker icons for the minimap canvas (pure, over Ctx2D).

import { playerColor } from '../lib/playerColors.js';
import { type PreparedBoard, tileAt } from './minimapBoard.js';
import { CORNER_COS, CORNER_SIN, SQRT3 } from './minimapGeometry.js';
import type { Ctx2D, PaintView } from './paintTypes.js';
import type { MinimapCityInfo, MinimapMarker, MinimapTile, MinimapUnitInfo } from './types.js';

const FALLBACK_UNIT = '#fcd34d';
const NEUTRAL_CITY = '#9ca3af';
const CITY_FILL = '#f8fafc';
const OUTLINE = 'rgba(0, 0, 0, 0.75)';

export function hexAt(ctx: Ctx2D, cx: number, cy: number, size: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const x = cx + size * (CORNER_COS[i] ?? 0);
    const y = cy + size * (CORNER_SIN[i] ?? 0);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** City icon: a filled hex with an owner-coloured ring; the capital is larger with a core dot. */
export function drawCity(
  ctx: Ctx2D,
  view: PaintView,
  cx: number,
  cy: number,
  info: MinimapCityInfo | undefined,
  tileOwner: number | null,
  colors: ReadonlyMap<number, string> | undefined,
): void {
  const capital = info?.isCapital === true;
  const neutral = info?.neutral === true;
  const owner = info ? info.ownerId : tileOwner;
  const ring =
    neutral || owner === null ? NEUTRAL_CITY : (colors?.get(owner) ?? playerColor(owner));
  const r = Math.max(2.4, view.scale * (capital ? 0.82 : 0.56));
  hexAt(ctx, cx, cy, r);
  ctx.fillStyle = neutral ? '#d1d5db' : CITY_FILL;
  ctx.fill();
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = Math.max(1, view.scale * 0.1) + 1.4;
  ctx.stroke();
  ctx.strokeStyle = ring;
  ctx.lineWidth = Math.max(1, view.scale * 0.1) + 0.2;
  ctx.stroke();
  if (capital) {
    ctx.fillStyle = ring;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.38, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Unit dot in the owner's colour; tucked into a corner when a city shares the tile. */
export function drawUnit(
  ctx: Ctx2D,
  view: PaintView,
  cx: number,
  cy: number,
  info: MinimapUnitInfo | undefined,
  tile: MinimapTile,
  colors: ReadonlyMap<number, string> | undefined,
): void {
  // The unit's own owner wins; the tile's owner is the fallback. `null` means
  // nobody owns the tile, so the dot keeps the neutral amber. A unit that
  // carries its own colour (a foreign house's vessel) paints that instead.
  const owner = info?.ownerId ?? tile.controlledBy;
  const color =
    info?.color ??
    (owner !== null && owner !== undefined
      ? (colors?.get(owner) ?? playerColor(owner))
      : FALLBACK_UNIT);
  const r = Math.max(1.6, view.scale * 0.3);
  const ox = tile.hasCity ? view.scale * 0.55 : 0;
  const oy = tile.hasCity ? view.scale * 0.5 : 0;
  ctx.beginPath();
  ctx.arc(cx + ox, cy + oy, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 1;
  ctx.stroke();
}

/** Champion-style overlay glyphs (default star), with an optional pulsing ring. */
export function drawMarkers(
  ctx: Ctx2D,
  board: PreparedBoard,
  view: PaintView,
  markers: readonly MinimapMarker[],
  phase: number,
  reducedMotion: boolean,
): void {
  const size = Math.min(24, Math.max(11, view.scale * 1.9));
  ctx.font = `700 ${size}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const m of markers) {
    const tile = tileAt(board, m.q, m.r);
    // Like every other layer: nothing on a tile that is not in the snapshot or is fog.
    if (!tile || tile.visibility === 'fog') continue;
    const cx = SQRT3 * (m.q + m.r / 2) * view.scale + view.panX;
    const cy = 1.5 * m.r * view.scale + view.panY;
    if (m.pulsing) {
      const k = reducedMotion ? 0 : (Math.sin(phase) + 1) / 2;
      ctx.beginPath();
      ctx.arc(cx, cy, size * (0.62 + 0.22 * k), 0, Math.PI * 2);
      ctx.strokeStyle = m.color;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.55 + 0.45 * (1 - k);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    const glyph = m.glyph ?? '★';
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 3;
    ctx.strokeText(glyph, cx, cy);
    ctx.fillStyle = m.color;
    ctx.fillText(glyph, cx, cy);
  }
}
